/**
 * Основные жанры фильмов и книг — общепринятые, как в кинотеатре и в книжном.
 *
 * Каталоги пишут в жанры всё подряд. Wikidata: «фильм-ограбление»,
 * «сериал-антология», «юношеское произведение искусства»; TMDB: «Музыка»;
 * Open Library: «orphans», «series:Harry_Potter». В фильтре это разрасталось в
 * десятки вариантов по одной-две записи. Поэтому из каталога берутся только
 * основные жанры, а длинные названия к ним сводятся: «криминальный
 * телесериал» — Криминал, «комедийная драма» — Комедия и Драма.
 *
 * Мультфильм, аниме и сериал здесь не жанры: это формат (`media-formats.ts`).
 *
 * Справочник лежит в `shared`: по нему приводятся и новые записи из каталога,
 * и лежавшие в базе, и восстановленные из копии.
 */

interface Genre {
  label: string;
  /** Проверяется по строке в нижнем регистре, с «е» вместо «ё». */
  match: RegExp;
}

const VOCABULARY: Genre[] = [
  {
    label: 'Фантастика',
    match: /фантаст|science fiction|sci-fi|киберпанк|антиутоп|постапокалип|космическая опера/,
  },
  { label: 'Фэнтези', match: /фэнтези|fantasy/ },
  { label: 'Мистика', match: /мистик|supernatural|paranormal/ },
  { label: 'Ужасы', match: /ужас|хоррор|horror/ },
  { label: 'Триллер', match: /триллер|thriller|саспенс/ },
  { label: 'Детектив', match: /детектив|mystery|detective/ },
  { label: 'Криминал', match: /криминал|гангстер|нуар|crime|noir/ },
  { label: 'Боевик', match: /боевик|экшн|экшен|боевых искусств|\baction\b/ },
  { label: 'Приключения', match: /приключен|adventure/ },
  { label: 'Комедия', match: /комеди|ситком|сатир|пароди|юмор|humou?r|comedy/ },
  { label: 'Мелодрама', match: /мелодрам|романтич|любовн|мыльн|romance/ },
  // «Мелодрама» — не драма.
  { label: 'Драма', match: /(?<!мело)драм|drama/ },
  { label: 'Военный', match: /военн|войн|\bwar\b/ },
  { label: 'Исторический', match: /историческ|^история$|history|historical/ },
  { label: 'Биография', match: /биограф|мемуар|biograph/ },
  { label: 'Вестерн', match: /вестерн|western/ },
  { label: 'Мюзикл', match: /мюзикл|musical/ },
  { label: 'Семейный', match: /семейн|family/ },
  { label: 'Для детей', match: /детск|детей|children|juvenile|kids/ },
  { label: 'Спорт', match: /спорт|sport/ },
  { label: 'Документальный', match: /документальн|documentary/ },
  // Книжные — целиком: «психологический триллер» — триллер, а не психология.
  { label: 'Психология', match: /^психология$|^psychology$/ },
  { label: 'Саморазвитие', match: /саморазвит|self-help|личностн/ },
  { label: 'Бизнес', match: /бизнес|business|экономик|economics|менеджмент|management/ },
  { label: 'Философия', match: /^философия$|^philosophy$/ },
  { label: 'Поэзия', match: /поэзи|стихотворен|poetry|poems/ },
  { label: 'Научно-популярное', match: /научно-популяр|popular science|^наука$|^science$/ },
];

/** Основные жанры по порядку. */
export const GENRES: readonly string[] = VOCABULARY.map((genre) => genre.label);

/** Основные жанры, к которым сводится одно название; не жанр — пусто. */
export function mainGenresOf(raw: string): string[] {
  const text = raw.trim().toLowerCase().replaceAll('ё', 'е');
  if (text.length === 0) return [];
  return VOCABULARY.filter((genre) => genre.match.test(text)).map((genre) => genre.label);
}

/**
 * Жанры записи из каталога — только основные, без повторов, в порядке
 * появления. То, что жанром не является, отбрасывается.
 */
export function canonicalGenres(raw: readonly string[]): string[] {
  return [...new Set(raw.flatMap(mainGenresOf))];
}
